import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatPrefix, MatSuffix } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { LookupListItem } from '@common/data-models/complex-types/lookup-list';
import { TeamMembersService } from '@common/services/team-members-service';
import { NavigationActions } from '@common/store/navigation/actions/navigation.actions';
import { NavigationItemTypes } from '@common/store/navigation/models/navigation.models';
import { selectCurrentItem, selectIsManageTeamMembers } from '@common/store/navigation/selectors/navigation.selectors';
import { CastListComponent } from '@src/suite/cast/components/cast-list/cast-list';
import { formatSearchBoxDisplayValue, getSearchDisplayValue } from '@src/suite/common/utils/util';
import { isSearchViewStateRecord } from '@src/suite/common/utils/viewstate-utils';
import { from, Observable, of, Subject, timer } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { NavigationService } from '../../services/navigation-service';
import { NomWebServicesJson } from '../../services/nom-web-services-json';
import { TeamMembersActions } from '../../store/team-members/team-members.actions';
import { AvatarBigComponent } from '../avatar/avatar-big';

@Component({
    selector: 'st-search-box',
    imports: [
        AsyncPipe,
        AvatarBigComponent,
        FormsModule,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatFormFieldModule,
        MatIcon,
        MatIconButton,
        MatInput,
        MatOption,
        MatPrefix,
        MatSuffix,
        MatTooltip,
        ReactiveFormsModule,
    ],
    templateUrl: './search-box.html',
    styleUrl: './search-box.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-search-box',
    },
})
export class SearchBoxComponent extends EnpsControlComponent<string> implements OnInit, AfterViewInit {
    teamMembersService = inject(TeamMembersService);
    navigationService = inject(NavigationService);
    nomWebServicesJson = inject(NomWebServicesJson);
    searchFilter = new FormControl<string | LookupListItem | Staff>('');

    executeSearchSubj = new Subject<string>();
    readonly executeCastSearch = output<{ term: string }>();

    readonly autocomplete = viewChild(MatAutocompleteTrigger);
    readonly searchInput = viewChild(MatInput);

    suggestions: Observable<(LookupListItem | Staff | string)[]>;
    public isStaffSearch = false;

    get allUsers(): Staff[] {
        return Object.values(this.globalTablesService.Staff);
    }

    readonly searchOpen = signal<boolean>(false);

    readonly item = computed(() => {
        return this.store.selectSignal(selectCurrentItem)();
    });

    readonly isManageTeamMembers = this.store.selectSignal(selectIsManageTeamMembers);

    constructor() {
        super();
        effect(() => {
            if (isSearchViewStateRecord(this.item()?.ViewState)) {
                this.searchFilter.patchValue((this.item().ViewState as StorytellingSearchViewStateRecord).PSTViewStateSearchQuery);
            } else {
                this.searchFilter.setValue('');
            }

            if (this.item() && this.item().Guid === NavigationItemTypes.SYSTEM_SEARCH_GUID && this.item().ViewState && isSearchViewStateRecord(this.item()?.ViewState)) {
                this.searchOpen.set(true);

                let query = (this.item().ViewState as StorytellingSearchViewStateRecord).PSTViewStateSearchQuery;

                if (query) {
                    query = decodeURIComponent(query);
                    if (query.startsWith('#')) {
                        this.isStaffSearch = false;
                        const tag = query.replace('#', '').toLowerCase();
                        if (tag) {
                            this.searchFilter.patchValue(new LookupListItem(tag, tag, tag));
                        }
                    } else if (query.startsWith('@')) {
                        this.isStaffSearch = true;
                        const staff = this.allUsers.find(option => option.UserID.toLowerCase() === query.replace('@', '').toLowerCase());
                        if (staff) {
                            this.searchFilter.patchValue(staff);
                        }
                    } else {
                        this.isStaffSearch = false;
                        this.searchFilter.patchValue(query);
                    }
                } else {
                    this.searchFilter.patchValue('');
                }
            } else {
                this.searchFilter.patchValue('');
                this.closeSearch();
            }
        });
    }

    ngOnInit() {
        this.suggestions = this.searchFilter.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            switchMap(value => this.getSuggestions(value)),
            takeUntil(this.destroyed$)
        );
        this.executeSearchSubj
            .pipe(
                debounceTime(300),
                switchMap(value => of(value)),
                takeUntil(this.destroyed$)
            )
            .subscribe(value => {
                this.executeSearch(value);
            });
    }

    public getSuggestions(val: string | LookupListItem | Staff): Observable<LookupListItem[] | Staff[]> {
        if (!val || typeof val === 'object') {
            this.isStaffSearch = false;
            return of([]);
        }

        val = val?.trim() as string;
        if (val?.length > 1) {
            if (val.startsWith('#') && val.length > 1) {
                val = val.replace('#', '').toLowerCase();
                this.isStaffSearch = false;
                return from(this.nomWebServicesJson.getSuggestions(val)).pipe(map(res => res.data.map(item => new LookupListItem(item, item, item))));
            }
            if (val.startsWith('@') && val.length > 2) {
                val = val.replace('@', '').toLowerCase();

                const data = this.allUsers
                    .filter(option => option.UserID.toLowerCase().includes(val as string) || `${option.GivenName} ${option.Surname}`.toLowerCase().includes(val as string))
                    .map(item => item);
                this.isStaffSearch = true;
                return of(data);
            }
        }
        this.isStaffSearch = false;
        return of([]);
    }

    displayFn(item: LookupListItem | Staff | string): string {
        if (item instanceof LookupListItem) {
            return `#${item.id}`;
        }
        if (typeof item === 'string') {
            if (item) {
                return formatSearchBoxDisplayValue(item, this.globalTablesService);
            } else {
                return '';
            }
        }
        return `@${item.GivenName} ${item.Surname}`;
    }

    getDisplayValue(item: LookupListItem | Staff | string): string {
        if (item instanceof LookupListItem) {
            return item.id;
        }

        if (typeof item === 'string') {
            return '';
        }

        return `${item.GivenName} ${item.Surname} ${item.Email ? item.Email : ''} ${item.Title ? item.Title : ''} ${item.Phone ? item.Phone : ''}`;
    }

    getUserId(item: LookupListItem | Staff | string): string {
        if (item instanceof LookupListItem || typeof item === 'string') {
            return null;
        }
        return item.UserID;
    }

    public onKeyDown(event: any) {
        // brackets ( ) shouldn't be allowed at all. we don't allow grouping at this level (and even if we do, it could mess solr query parsing and grouping)
        if (event.key === '(' || event.key === ')') {
            return false;
        }
    }

    public onKeyUp(event: any) {
        if (event.keyCode === 13 || event.key === 'Enter') {
            this.searchFn();
        }
    }

    parseSearchValue(value: string | LookupListItem | Staff): string {
        if (value instanceof LookupListItem) {
            return `#${value.id}`;
        }
        if (typeof value === 'string') {
            if (value?.startsWith('@')) {
                return null;
            } else {
                return value?.trim();
            }
        }
        return `@${value.UserID}`;
    }

    optionSelected(event: MatAutocompleteSelectedEvent) {
        const value = this.parseSearchValue(event.option.value);
        if (!value) {
            return;
        }
        this.executeSearchSubj.next(value);
    }

    private async executeSearch(val: string) {
        if (!val?.startsWith('#') && !val?.startsWith('@') && this.isManageTeamMembers()) {
            this.store.dispatch(TeamMembersActions.filterTeamMembers({ term: val }));
        } else if (this.navigationService.currentListView instanceof CastListComponent) {
            this.executeCastSearch.emit({ term: val });
        } else {
            this.store.dispatch(NavigationActions.enterSearchMode({ term: val, searchDisplayValue: getSearchDisplayValue(val, this.globalTablesService) }));
        }
    }

    public closeSearch() {
        this.searchOpen.set(false);
        this.autocomplete().closePanel();
    }

    public openSearch() {
        if (this.searchOpen()) {
            this.searchFn();
        } else {
            this.searchOpen.set(true);
        }
        timer(0).subscribe(() => {
            this.searchInput()?.focus();
            this.autocomplete().closePanel();
            this.autocomplete().updatePosition();
        });
    }

    private async searchFn() {
        const { value } = this.searchFilter;
        const searchTerm: string = this.parseSearchValue(value);
        if (!searchTerm || searchTerm.trim().length === 0) {
            // Empty search
            if (this.isManageTeamMembers()) {
                this.store.dispatch(TeamMembersActions.filterTeamMembers({ term: null }));
            }
            return;
        }
        this.executeSearchSubj.next(searchTerm);
    }
}
